import api, { ApiReturn, GetApiReturn, GetApiReturnError, ValidateAxiosError, } from '@/modules/api.service';
import { ConfiguracoesSistema } from '@/modules/business/movimentacoes/enum/configuracoes-sistema.enum';
import { readConfiguracoes } from '@/modules/system/configuracoes/configuracoes.service';
import { Endpoints } from '@/shared/constants/endpoints';
import { ReadPeriodoDto, CreatePeriodoDto, UpdatePeriodoDto, ReadSubstituicaoDto } from '@/api.types';

/**
 * Lista de períodos do defensor.
 * @param {number} defensor Id do defensor.
 * @param {number} tipo Tipo.
 * @param {number} skip Itens para pular.
 * @param {number} take Itens para trazer.
 * @returns Lista de Periodos de um defensor.
 */
export async function GetPeriodos(
  defensor: number,
  tipo: number,
  skip: number,
  take: number,
) {
  if (!tipo) tipo = 0;

  return api
    .get(Endpoints.PERIODO, {
      params: { defensor: defensor, tipo: tipo, skip: skip, take: take },
    })
    .then((response) => {
      return GetApiReturn<ReadPeriodoDto>(response.data as Array<ReadPeriodoDto>);
    })
    .catch((error) => {
      ValidateAxiosError<ReadPeriodoDto>(
        error,
        'Falha ao listar períodos do defensor.',
      );
      return GetApiReturnError<ReadPeriodoDto>();
    });
}

/**
 * Lista de períodos do defensor (rota sem restrição de permissão).
 * Usada para carregar impedimentos no calendário de relatório.
 * @param {number} defensorId Id do defensor.
 * @param {number} tipo Tipo (opcional).
 * @returns Lista de Periodos de um defensor.
 */
export async function GetPeriodosDoDefensor(
  defensorId: number,
  tipo?: number,
): Promise<ApiReturn<ReadPeriodoDto>> {
  const params: { tipo?: number } = {};
  if (tipo !== undefined) params.tipo = tipo;

  return api
    .get(`${Endpoints.PERIODO}/by-defensor/${defensorId}`, { params })
    .then((response) => {
      return response.data as ApiReturn<ReadPeriodoDto>;
    })
    .catch((error) => {
      console.error('Erro ao buscar períodos do defensor:', error);
      return { objects: [], count: 0 };
    });
}

/**
 * Lista de períodos do defensor por CPF (rota sem restrição de permissão).
 * Usada para carregar impedimentos no calendário de relatório via login token.
 * @param {string} cpf CPF do defensor.
 * @param {number} tipo Tipo (opcional).
 * @returns Lista de Periodos de um defensor.
 */
export async function GetPeriodosDoDefensorPorCpf(
  cpf: string,
  tipo?: number,
): Promise<ApiReturn<ReadPeriodoDto>> {
  const params: { tipo?: number } = {};
  if (tipo !== undefined) params.tipo = tipo;

  return api
    .get(`${Endpoints.PERIODO}/by-cpf/${cpf}`, { params })
    .then((response) => {
      return response.data as ApiReturn<ReadPeriodoDto>;
    })
    .catch((error) => {
      console.error('Erro ao buscar períodos do defensor por CPF:', error);
      return { objects: [], count: 0 };
    });
}

/**
 * Retorna um período de acordo com id.
 * @param {number} id
 * @returns Periodo ou null.
 */
export async function GetPeriodo(id: number) {
  return api
    .get<ReadPeriodoDto>(Endpoints.PERIODO + '/' + id)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return ValidateAxiosError<ReadPeriodoDto>(
        error,
        `Falha ao buscar o período: ${id}`,
      );
    });
}

/**
 * Lista os períodos aquisitivos de férias.
 * @param {Date} data_admissao
 * @returns Data Admissão.
 */
export async function GetDatasAquisitivasFerias(data_admissao: Date) {
  return api
    .get<Date[]>(Endpoints.PERIODO + '/datasaquisitivasferias/' + data_admissao)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return ValidateAxiosError<Date[]>(
        error,
        `Falha ao buscar períodos aquisitivos: ${data_admissao}`,
      );
    });
}

/**
 * Lista
 * @param {Date} data_admissao
 * @returns Data admissão.
 */
export async function GetDatasAquisitivasLicencaPremio(data_admissao: Date) {
  return api
    .get<Date[]>(
      Endpoints.PERIODO + '/datasaquisitivaslicencapremio/' + data_admissao,
    )
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return ValidateAxiosError<Date[]>(
        error,
        `Falha ao buscar períodos aquisitivos: ${data_admissao}`,
      );
    });
}

/**
 * Lista anos de referência disponíveis para abono especial.
 */
export async function GetDatasAquisitivasAbonoEspecial(data_admissao: Date) {
  return api
    .get<Date[]>(
      Endpoints.PERIODO + '/datasaquisitivasabonoespecial/' + data_admissao,
    )
    .then((response) => response.data)
    .catch((error) =>
      ValidateAxiosError<Date[]>(
        error,
        `Falha ao buscar anos de referência: ${data_admissao}`,
      ),
    );
}

/**
 * Adiciona ou altera um período, se o id for superior a 0 será um update, caso contrário uma inserção.
 * @param {CreatePeriodoDto | UpdatePeriodoDto} periodo
 * @returns True se salvo, False em caso contrário.
 */
export async function SetPeriodo(periodo: CreatePeriodoDto | UpdatePeriodoDto) {
  if (periodo.id !== null && periodo.id !== undefined && periodo.id > 0) {
    return api
      .put(Endpoints.PERIODO + '/' + periodo.id, periodo as UpdatePeriodoDto)
      .then(() => {
        return 'Período atualizado.';
      })
      .catch((error) => {
        console.error(error.response.data.message);
        throw new Error(error.response.data.message);
      });
  } else {
    return api
      .post(Endpoints.PERIODO, periodo as CreatePeriodoDto)
      .then(() => {
        return 'Período cadastro com sucesso.';
      })
      .catch((error) => {
        console.error(error.response.data.message);
        throw new Error(error.response.data.message);
      });
  }
}

/**
 * Apaga um período do sistema.
 * @param {number} id
 * @returns True se apagou, False em caso contrário.
 */
export async function DeletePeriodo(id: number) {
  return await api
    .delete(Endpoints.PERIODO + '/' + id)
    .then(() => {
      return 'Período removido.';
    })
    .catch((error) => {
      return ValidateAxiosError<string>(error, 'Falha ao remover período.');
    });
}

/**
 * Buscas as substituições do período.
 * @param periodoid Identificação do período.
 * @returns
 */
export async function GetSubstituicoesPeriodos(
  periodoid: number,
  exibirTudo: boolean = false,
) {
  if (periodoid != undefined && periodoid != null) {
    return api
      .get(Endpoints.SUBSTITUICAO + '/doperiodo/' + periodoid + '?exibirtudo=' + exibirTudo)
      .then((response) => {
        return GetApiReturn<ReadSubstituicaoDto>(response.data as Array<ReadSubstituicaoDto>);
      })
      .catch((error) => {
        ValidateAxiosError<ReadSubstituicaoDto>(
          error,
          'Falha ao listar substituicoes do período.',
        );
        return GetApiReturnError<ReadSubstituicaoDto>();
      });
  } else {
    const r: ApiReturn<ReadSubstituicaoDto> = { objects: [], count: 0 };
    return r;
  }
}

export interface PontuacaoEvento {
  tipo: 'periodo' | 'substituicao';
  id: number;
  data_inicial?: string;
  data_final?: string;
  motivo?: string;
  anotacao?: string;
  tipo_periodo?: number;
  defensoria?: string;
}

export interface PontuacaoDefensor {
  defensor: { id: number; nome: string };
  totalPontos: number;
  eventos: PontuacaoEvento[];
}

/**
 * Busca a lista de defensores com pontuações.
 * @returns Lista de defensores com seus pontos e eventos.
 */
export async function carregarAnoInicioFeriasSessenta(): Promise<number | null> {
  const rows = await readConfiguracoes();
  const valor = rows
    .find((row) => row.chave === ConfiguracoesSistema.FeriasSessentaDiasAPartirDoAno)
    ?.valor?.trim();
  if (!valor || !/^\d{4}$/.test(valor)) return null;
  const ano = Number(valor);
  if (ano < 1900 || ano > 2100) return null;
  return ano;
}

export async function GetPontuacoes(): Promise<PontuacaoDefensor[]> {
  return api
    .get(Endpoints.PERIODO + '/pontuacoes/lista')
    .then((response) => {
      return response.data as PontuacaoDefensor[];
    })
    .catch((error) => {
      ValidateAxiosError<PontuacaoDefensor[]>(
        error,
        'Falha ao buscar pontuações.',
      );
      return [];
    });
}